import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const news = newsList.find((n) => n.id === id);

  useDocumentTitle(
    news ? `${news.title} - 动漫网站资讯` : "动漫资讯详情 - 动漫网站",
    news ? news.summary : "动漫资讯详情页"
  );

  const related = useMemo(() => {
    if (!news) return [];
    return newsList
      .filter((n) => n.id !== news.id)
      .sort((a, b) => (a.category === news.category ? -1 : 0) - (b.category === news.category ? -1 : 0))
      .slice(0, 4);
  }, [news]);

  if (!news) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-20 text-center">
        <i className="ri-error-warning-line text-3xl text-foreground-400" />
        <h1 className="font-heading font-semibold text-xl text-foreground-900 mt-4">未找到该资讯</h1>
        <Link
          to="/news"
          className="inline-block mt-5 h-10 px-5 leading-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "动漫资讯", to: "/news" }, { label: news.title }]} />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
        <article className="min-w-0">
          <span className="inline-block px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
            {news.category}
          </span>
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mt-3 leading-snug">
            {news.title}
          </h1>
          <div className="flex items-center flex-wrap gap-4 mt-4 text-sm text-foreground-400 pb-6 border-b border-background-200/70">
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line" />{news.publishedAt}</span>
            <span className="flex items-center gap-1.5"><i className="ri-eye-line" />{news.views.toLocaleString()} 次阅读</span>
            <span className="flex items-center gap-1.5"><i className="ri-user-line" />动漫网站编辑部</span>
          </div>

          <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden bg-background-200 mt-6">
            <img
              src={news.cover}
              alt={news.title}
              title={`${news.title} - 动漫资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-7 space-y-5">
            {news.content.map((paragraph, index) => (
              <p
                key={index}
                className={`text-[15px] md:text-base text-foreground-700 leading-[1.9] ${
                  index === 0 ? "font-medium text-foreground-800" : ""
                }`}
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 p-4 rounded-lg bg-secondary-50 border border-secondary-200/70 text-sm text-foreground-600">
            <i className="ri-information-line mr-1.5 text-accent-600" />
            本文内容为演示资讯，仅用于展示动漫资讯平台的页面效果，不代表真实报道。
          </div>

          <Link
            to="/news"
            className="inline-flex items-center gap-2 mt-8 h-10 px-5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
        </article>

        <aside className="lg:sticky lg:top-24 h-fit">
          <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">相关文章推荐</h2>
          <div className="flex flex-col gap-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all"
              >
                <div className="w-20 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 动漫资讯`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm text-foreground-900 group-hover:text-primary-600 transition-colors clamp-2 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-1.5">{item.publishedAt}</p>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}